import { Injectable, signal } from '@angular/core';

export type PdfEditToolType = 'text' | 'checkbox' | 'date' | 'esign' | 'comment' | 'initials' | 'stamp';


export interface PdfEditSelectedAnnotation {
  element: HTMLElement;
  type: string;
  properties: { placeholder?: string; fontSize?: number; user?: string; page?: number; [key: string]: any };
}

export interface PdfEditAnnotationHandlers {
  updateProperty: (property: string, value: any) => void;
  copyToPages: (option: string) => void;
}

@Injectable({
  providedIn: 'root',
})
export class PdfEditStateService {

  readonly showToolsInHeader = signal<boolean>(false);

  readonly mobileToolsExpanded = signal<boolean>(false);


  readonly selectedTool = signal<PdfEditToolType | null>(null);


  readonly selectedAnnotation = signal<PdfEditSelectedAnnotation | null>(null);


  readonly propertyDrawerOpen = signal<boolean>(false);

  private _handlers: PdfEditAnnotationHandlers | null = null;

  setShowToolsInHeader(show: boolean): void {
    this.showToolsInHeader.set(show);
    if (!show) this.mobileToolsExpanded.set(false);
  }

  toggleMobileToolsExpanded(): void {
    this.mobileToolsExpanded.update(v => !v);
  }

  setSelectedTool(tool: PdfEditToolType | null): void {
    this.selectedTool.set(tool);
  }


  toggleTool(tool: PdfEditToolType): void {
    const current = this.selectedTool();
    this.selectedTool.set(current === tool ? null : tool);
  }

  setSelectedAnnotation(annotation: PdfEditSelectedAnnotation | null): void {
    this.selectedAnnotation.set(annotation);
  }

  setPropertyDrawerOpen(open: boolean): void {
    this.propertyDrawerOpen.set(open);
  }

  /** Scroll the property panel into view after a field is placed or selected. */
  schedulePropertyPanelScroll(): void {
    if (typeof window === 'undefined') return;
    const isMobile = window.innerWidth <= 1024;
    const delay = isMobile ? 280 : 60;
    window.setTimeout(() => this.scrollPropertyPanelIntoView(), delay);
  }

  scrollPropertyPanelIntoView(): void {
    if (typeof window === 'undefined') return;

    const panelSelectors = [
      '.create-sign-stepper__props-panel',
      '.signature-properties-panel-outside',
    ];

    let panel: HTMLElement | null = null;
    for (const selector of panelSelectors) {
      panel = document.querySelector(selector) as HTMLElement | null;
      if (panel) break;
    }
    if (!panel) return;

    const wrapper = panel.closest(
      '.create-sign-stepper__props-wrapper, .signature-props-drawer',
    ) as HTMLElement | null;

    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 2) {
      const wrapperRect = wrapper.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const targetTop = panelRect.top - wrapperRect.top + wrapper.scrollTop - 8;
      wrapper.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
      return;
    }

    panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  registerAnnotationHandlers(handlers: PdfEditAnnotationHandlers): void {
    this._handlers = handlers;
  }

  unregisterAnnotationHandlers(): void {
    this._handlers = null;
  }

  updateAnnotationProperty(property: string, value: any): void {
    this._handlers?.updateProperty(property, value);
  }

  copyAnnotationToPages(option: string): void {
    this._handlers?.copyToPages(option);
  }
}
